import type { EditorProps } from '../types';
import { APP_CONSTANTS } from '~/common/explicit-exports';

const generateFontFamilyFormats = () => {
  const fonts = APP_CONSTANTS?.GOOGLE_FONTS || [];
  return fonts
    .map((font) => `${font}=${font}`)
    .sort()
    .join(';');
};

const BASE_MODE = {
  enableVersioning: false,
  plugins: ['autolink', 'autoresize', 'code', 'codesample', 'image', 'media', 'link', 'lists', 'table'],
  toolbar: [
    'blocks fontsize fontFamily',
    'bold italic additionalformatting removeformat forecolor backcolor',
    'align bullist numlist indent outdent',
    'link image media hr table',
    'code',
    'versionhistory',
  ].join(' | '),
  init: {
    contextmenu: 'image editimage linkchecker lists configurepermanentpen spellchecker table',
    elementpath: false,
    toolbar_mode: 'floating',
    toolbar_sticky: false,
    statusbar: false,
    autoresize_bottom_margin: 1,
    height: 60,
    menubar: false,
    branding: false,
    image_caption: true,
    browser_spellcheck: true,
    min_height: 250,
    highlight_on_focus: false,
    sandbox_iframes: false,
    block_formats:
      'Normal text=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6; Blockquote=blockquote; Code=pre;',
    font_size_formats: '8px 10px 12px 14px 16px 18px 20px 24px 36px 48px',
    font_family_formats: generateFontFamilyFormats(), // Dynamically set font family formats
    toolbar_groups: {
      additionalformatting: {
        icon: 'image-options',
        tooltip: 'Formatting',
        items: 'underline strikethrough removeformat',
      },
    },
    paste_remove_styles_if_webkit: false,
  },
} satisfies EditorProps;

export const TEXT_EDITOR_MODES = {
  TEXT_CONTENT: {
    ...BASE_MODE,
    enableVersioning: true,
    init: {
      ...BASE_MODE.init,
      ui_mode: 'split',
      toolbar_sticky: true,
      toolbar_sticky_offset: 56,
    },
  },
  TEXT_CONTENT_ACCORDION: {
    ...BASE_MODE,
    enableVersioning: false,
    init: {
      ...BASE_MODE.init,
      ui_mode: 'split',
      toolbar_sticky: true,
      toolbar_sticky_offset: 56,
    },
  },
  TEXT_CONTENT_RESOURCE: {
    ...BASE_MODE,
    enableVersioning: true,
    init: {
      ...BASE_MODE.init,
      ui_mode: 'split',
      toolbar_sticky: true,
      toolbar_sticky_offset: 64,
    },
  },
  TEXT_AREA_CONTENT: {
    ...BASE_MODE,
    init: {
      ...BASE_MODE.init,
      height: 250,
      toolbar_mode: 'sliding',
      automatic_uploads: false,
    },
    plugins: ['autolink', 'autoresize', 'code', 'codesample', 'image', 'link', 'lists', 'table'],
    toolbar: [
      'blocks fontsize',
      'bold italic underline strikethrough removeformat',
      'align bullist numlist indent outdent',
      'link image media hr table',
    ].join(' | '),
  },
  THEME_EDITOR: {
    ...BASE_MODE,
    init: {
      ...BASE_MODE.init,
      min_height: 100,
      max_height: 300,
    },
    toolbar: ['bold italic underline link'],
  },
  EMAIL_BODY_EDITOR: {
    ...BASE_MODE,
    init: {
      ...BASE_MODE.init,
      height: 250,
      toolbar_mode: 'sliding',
      automatic_uploads: false,
      paste_data_images: false,
      invalid_elements: 'img,picture,video,audio,iframe,object,embed,source,track',
    },
    plugins: ['autolink', 'autoresize', 'link', 'lists'],
    toolbar: ['blocks fontsize', 'bold italic underline forecolor', 'align bullist link'].join(' | '),
  },
  ACADEMY_CONTENT_SETTINGS_EDITOR: {
    ...BASE_MODE,
    init: {
      ...BASE_MODE.init,
      height: 250,
      toolbar_mode: 'sliding',
      automatic_uploads: true,
      paste_data_images: true,
      invalid_elements: 'video,audio,object,embed,source,track',
    },
    plugins: ['autolink', 'autoresize', 'link', 'lists', 'code', 'code', 'image', 'media', 'table'],
    toolbar: [
      'blocks fontsize',
      'bold italic underline strikethrough forecolor',
      'code image media align bullist link table',
    ].join(' | '),
  },
  PATH_DESCRIPTION_EDITOR: {
    ...BASE_MODE,
    toolbar: [
      'blocks fontsize forecolor',
      'bold italic underline strikethrough removeformat',
      'align bullist numlist indent outdent',
      'link image media hr',
    ].join(' | '),
  },
  EVENT_DESCRIPTION_EDITOR: {
    ...BASE_MODE,
  },
  CHALLENGE_INSTRUCTION_EDITOR: {
    ...BASE_MODE,
  },
  CHALLENGE_WRITTEN_SUBMISSION_EDITOR: {
    ...BASE_MODE,
    toolbar: [
      'blocks fontsize',
      'bold italic underline strikethrough removeformat',
      'align bullist numlist',
      'link image media hr table',
    ].join(' | '),
  },
  NOTES_EDITOR: {
    ...BASE_MODE,
    toolbar: [
      'bold italic additionalformatting forecolor backcolor',
      'align bullist numlist indent outdent',
      'link hr table',
      'code',
    ].join(' | '),
  },
  FLIP_CARD_EDITOR: {
    ...BASE_MODE,
    plugins: [...BASE_MODE.plugins, 'quickbars'],
    init: {
      ...BASE_MODE.init,
      min_height: 98,
      quickbars_insert_toolbar: false,
      quickbars_image_toolbar: ['alignleft aligncenter alignright', 'link'].join(' | '),
      quickbars_selection_toolbar: [
        'blocks',
        'bold italic underline strikethrough removeformat',
        'align bullist numlist indent outdent',
        'link image',
      ].join(' | '),
    },
    toolbar: false,
  },
  CALLOUT_EDITOR: {
    ...BASE_MODE,
    plugins: [...BASE_MODE.plugins, 'quickbars'],
    init: {
      ...BASE_MODE.init,
      min_height: 60,
      quickbars_insert_toolbar: false,
      quickbars_image_toolbar: ['alignleft aligncenter alignright', 'link'].join(' | '),
      quickbars_selection_toolbar: [
        'blocks',
        'bold italic underline strikethrough removeformat',
        'align bullist numlist indent outdent',
        'link image',
      ].join(' | '),
    },
    toolbar: false,
  },
  ACADEMY_REGISTRATION_PAGE_EDITOR: {
    ...BASE_MODE,
    toolbar: ['bold italic underline strikethrough', 'link'].join(' | '),
    init: {
      ...BASE_MODE.init,
      min_height: 128,
    },
  },
  LIBRARY_DESCRIPTION_EDITOR: {
    ...BASE_MODE,
    toolbar: [
      'blocks fontsize forecolor',
      'bold italic underline strikethrough removeformat',
      'align bullist numlist indent outdent hr',
      'link image',
    ].join(' | '),
    init: {
      ...BASE_MODE.init,
      min_height: 128,
    },
  },
} satisfies { [key: string]: EditorProps };
